import React, { ChangeEvent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import { LuLock, LuPhone, LuUser } from "react-icons/lu";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { NewLogin } from "./NewLogin";
import { useOtpSendMutation } from "../../Redux/Api/Signup";
import useCiT from "../../common/ci/i18n";
import { AuthField, AuthHeading, AuthShell, AuthTabs, EyeToggle } from "./AuthShell";

type SignupForm = {
  name: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
};

type Errors = Partial<Record<keyof SignupForm, string>>;

/**
 * /signup — ek hi page par Sign in aur New account (tabs).
 * Signup ke baad OTP page (/otp-verification) par phone verify hota hai.
 */
const AuthForm: React.FC = () => {
  const t = useCiT();
  const location = useLocation();
  const navigate = useNavigate();

  const [tab, setTab] = useState<"in" | "up">("in");
  const [formData, setFormData] = useState<SignupForm>({
    name: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<Errors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [mutation, { data, isError, isSuccess, isLoading, error }] = useOtpSendMutation();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const next = name === "phoneNumber" ? value.replace(/\D/g, "").slice(0, 10) : value;
    setFormData((prev) => ({ ...prev, [name]: next } as SignupForm));
    setErrors((prev) => ({ ...prev, [name]: "" } as Errors));
  };

  const validate = (): Errors => {
    const { name, phoneNumber, password, confirmPassword } = formData;
    const e: Errors = {};
    if (!name.trim()) e.name = t("Naam zaroori hai");
    if (!phoneNumber) e.phoneNumber = t("Phone number zaroori hai");
    else if (!/^[6-9]\d{9}$/.test(phoneNumber)) e.phoneNumber = t("10 ank ka sahi mobile number daalein");
    if (!password) e.password = t("Password zaroori hai");
    else if (password.length < 6) e.password = t("Password kam se kam 6 akshar ka ho");
    if (password && confirmPassword !== password) e.confirmPassword = t("Dono password alag hain");
    return e;
  };

  const onSignUp = () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    const { name, phoneNumber, password } = formData;
    mutation({ name: name.trim(), phoneNumber, password });
  };

  const onSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    onSignUp();
  };

  useEffect(() => {
    if (isSuccess) {
      toast.success(data?.message || t("OTP bhej diya"));
      if (!localStorage.getItem("otpTimerStart")) {
        localStorage.setItem("otpTimerStart", Date.now().toString());
      }
      navigate("/otp-verification", {
        state: {
          phoneNumber: formData?.phoneNumber,
          name: formData?.name,
          password: formData?.password,
        },
      });
    } else if (isError) {
      toast.error(error?.data?.message || t("Kuch galat ho gaya"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isSuccess]);

  useEffect(() => {
    if (tab === "in") {
      setFormData({ name: "", phoneNumber: "", password: "", confirmPassword: "" });
      setErrors({});
    }
  }, [tab]);

  useEffect(() => {
    if (!location.pathname.startsWith("/otp-verification")) {
      localStorage.removeItem("otpTimerStart");
    }
  }, [location.pathname]);

  return (
    <AuthShell side>
      <AuthTabs active={tab} onChange={setTab} />

      {tab === "in" ? (
        <NewLogin handleClick={() => setTab("up")} signUp={false} />
      ) : (
        <form onSubmit={onSubmit} noValidate>
          <AuthHeading
            title={t("New account")}
            sub={t("Phone number par OTP aayega. Yahi number aage aapka login ID rahega.")}
          />

          <div className="flex flex-col gap-4">
            <AuthField
              label={t("Naam")}
              name="name"
              type="text"
              autoComplete="name"
              placeholder={t("Aapka ya firm ka naam")}
              icon={<LuUser size={18} />}
              value={formData.name}
              onChange={handleChange}
              error={errors.name}
            />

            <AuthField
              label={t("Mobile number")}
              name="phoneNumber"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              maxLength={10}
              prefix="+91"
              placeholder="98xxxxxxxx"
              icon={<LuPhone size={18} />}
              value={formData.phoneNumber}
              onChange={handleChange}
              error={errors.phoneNumber}
              hint={t("OTP isi par aayega")}
            />

            <AuthField
              label={t("Password")}
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder={t("Kam se kam 6 akshar")}
              icon={<LuLock size={18} />}
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              right={<EyeToggle shown={showPassword} onToggle={() => setShowPassword((p) => !p)} />}
            />

            <AuthField
              label={t("Password dobara")}
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder={t("Wahi password dobara")}
              icon={<LuLock size={18} />}
              value={formData.confirmPassword}
              onChange={handleChange}
              error={errors.confirmPassword}
              right={
                <EyeToggle
                  shown={showConfirmPassword}
                  onToggle={() => setShowConfirmPassword((p) => !p)}
                />
              }
            />

            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-md border border-ci-orange bg-ci-orange text-[17px] font-bold uppercase tracking-wide text-white transition hover:border-ci-orange2 hover:bg-ci-orange2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLoading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  {t("OTP bhej rahe hain…")}
                </>
              ) : (
                t("OTP bhejein")
              )}
            </button>

            <p className="text-center text-[14px] text-ci-muted">
              {t("Pehle se account hai?")}{" "}
              <button
                type="button"
                onClick={() => setTab("in")}
                className="font-semibold text-ci-navy underline underline-offset-2"
              >
                {t("Sign in karein")}
              </button>
            </p>
          </div>
        </form>
      )}
    </AuthShell>
  );
};

export default AuthForm;
